import { TILE } from './constants';
import { unitDef } from '../content/units';
import { buildingDef } from '../content/buildings';
import type { Building, GameState, Unit } from './types';

/*
 * ראייה וערפל קרב.
 *
 * **המסכה נגזרת ואינה נשמרת.** היא פונקציה טהורה של מיקומי היחידות והמבנים בטיק
 * הנוכחי, ולכן היא לא נכנסת ל-hash ולא לסריאליזציה: שדה שאפשר לחשב מחדש הוא שדה
 * שאפשר לשכוח לעדכן. מה שכן נשמר הוא **מה שכבר נחשף** (`explored`), כי זו זיכרון
 * ואי אפשר לגזור אותו ממצב רגעי.
 *
 * שתי שכבות, כמו ב-RA2:
 *   - **צל** (shroud) — אריח שמעולם לא ראית. שחור מלא, אין בו מידע.
 *   - **ערפל** (fog) — ראית פעם ואינך רואה עכשיו. הטרשת והמבנים שראית נשארים,
 *     היחידות **לא** — הן זזו מאז, וזיכרון של יחידה הוא שקר.
 *
 * הרדיוס מגיע מ-`sightRange` שכבר קיים בתוכן ומשמש לרכישת מטרות. **אותו מספר**,
 * ולא שדה שני שיסתור אותו: יחידה שרואה רחוק יותר ממה שהיא יורה היא החלטת תוכן,
 * ומי שירצה אותה ישנה מספר אחד במקום להוסיף מנגנון.
 */

/** מסכת ראייה: בית לאריח. 1 = נראה עכשיו. */
export type VisionMask = Uint8Array;

/**
 * ראייה מינימלית ליחידה חסרת נשק. קוצר עם `sightRange: 0` היה נוסע בתוך עיוורון
 * מוחלט, והשחקן היה מאבד אותו ברגע שהוא יוצא מהבסיס.
 */
const MIN_SIGHT = 4 * TILE;

function stamp(mask: VisionMask, w: number, h: number, x: number, y: number, range: number): void {
    const r = Math.max(MIN_SIGHT, range);
    const tiles = Math.floor(r / TILE);
    const cx = Math.floor(x / TILE);
    const cy = Math.floor(y / TILE);
    const r2 = tiles * tiles;
    const minY = Math.max(0, cy - tiles);
    const maxY = Math.min(h - 1, cy + tiles);
    const minX = Math.max(0, cx - tiles);
    const maxX = Math.min(w - 1, cx + tiles);
    for (let ty = minY; ty <= maxY; ty++) {
        const dy = ty - cy;
        const row = ty * w;
        for (let tx = minX; tx <= maxX; tx++) {
            const dx = tx - cx;
            // עיגול ולא ריבוע — מלבן ראייה נראה כמו באג ברגע שרואים אותו על המסך.
            if (dx * dx + dy * dy <= r2) mask[row + tx] = 1;
        }
    }
}

/**
 * מה שהשחקן רואה **עכשיו**.
 *
 * `out` מאפשר לקורא לתחזק מאגר משלו ולא להקצות 9,216 בתים בכל טיק — הרנדרר קורא
 * לזה בכל פריים.
 */
export function visibleMask(
    state: GameState,
    player: number,
    w: number,
    h: number,
    out?: VisionMask,
): VisionMask {
    const mask = out && out.length === w * h ? out : new Uint8Array(w * h);
    mask.fill(0);

    for (const unit of state.units as readonly Unit[]) {
        if (unit.owner !== player || unit.hp <= 0) continue;
        stamp(mask, w, h, unit.x, unit.y, unitDef(unit.type).sightRange);
    }

    for (const building of state.buildings as readonly Building[]) {
        if (building.owner !== player || building.hp <= 0) continue;
        const def = buildingDef(building.type);
        // ממרכז המבנה ולא מפינתו — מפקדה של 4×4 הייתה רואה רק רבע מסביבתה.
        const cx = building.tileX * TILE + (def.w * TILE) / 2;
        const cy = building.tileY * TILE + (def.h * TILE) / 2;
        stamp(mask, w, h, cx, cy, def.sightRange);
    }

    return mask;
}

/**
 * מוסיף את מה שנראה עכשיו לזיכרון המצטבר.
 *
 * מחזיר את **אותו מערך** כשלא התגלה דבר. זה לא חיסכון קוסמטי: הרנדרר משווה
 * הפניה כדי לדעת אם לצייר מחדש את שכבת הערפל, ומערך חדש בכל טיק היה מבטל את
 * המטמון — בדיוק הבאג שנמצא בשכבת העפרות (מלכודת 52).
 */
export function addExplored(explored: Uint8Array, visible: VisionMask): Uint8Array {
    let changed = false;
    for (let i = 0; i < visible.length; i++) {
        if (visible[i] === 1 && explored[i] === 0) {
            if (!changed) {
                changed = true;
                explored = Uint8Array.from(explored);
            }
            explored[i] = 1;
        }
    }
    return explored;
}

/** האם נקודת עולם נראית לשחקן כרגע. */
export function seesPoint(mask: VisionMask, w: number, x: number, y: number): boolean {
    const tx = Math.floor(x / TILE);
    const ty = Math.floor(y / TILE);
    if (tx < 0 || ty < 0) return false;
    const index = ty * w + tx;
    return index < mask.length && mask[index] === 1;
}
